import React, { useEffect, useMemo, useState } from "react";
import { useImmer } from "use-immer";
import { barChartOptons, chartColorMap } from "../../dfccilUtils.js/dfccilConsts";
import { Chart, Doughnut } from "react-chartjs-2";
import "./ChartChainComponent.scss";
import { APP_BASE_URL } from "../../../../Common/EndPoints";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectedCustomersIdsSelector } from "../../../../features/authentication/auth-slice";
import { createSearchParams, useNavigate } from "react-router-dom";
import { Button, ButtonGroup, IconButton, Stack } from "@mui/material";
import PieChartIcon from "@mui/icons-material/PieChart";
import BarChartIcon from "@mui/icons-material/BarChart";

const chartChainTypes = ["stn", "dept", "type"];
const chartDataFormat = {
  labels: [],
  datasets: [
    {
      label: "count",
      data: [],
      backgroundColor: "blue",
    },
  ],
};
const initialChartData = {
  stn: { bar: chartDataFormat, doughnut: chartDataFormat },
  dept: { bar: chartDataFormat, doughnut: chartDataFormat },
  type: { bar: chartDataFormat, doughnut: chartDataFormat },
};

const headingMapForParentId = {
  sfr: "Failure incidences",
  maintenace: "Maintenance Overdue",
  out_of_range: "Maintenance Report",
};
const headingMapForChartType = {
  stn: "- Stations",
  dept: "- Department",
  type: "- Asset type",
};

export const generateColorFromNumber = (num) => {
  // Create pseudo-random RGB values by hashing the number
  const r = (num * 123) % 255;
  const g = (num * 456) % 255;
  const b = (num * 789) % 255;
  return `rgba(${r}, ${g}, ${b}, 0.7)`;
};

/**
 * @param {string} chartVariant - "bar" or "doughnut"
 */
const ChartChainChild = ({ chartType, chartData, chartOptions, onCloseChart, parentId, chartVariant = "doughnut" }) => {
  const { labels = [] } = chartData?.[chartVariant];

  const getHeading = (parentId, chartType) => {
    let heading = "";
    if (chartData[chartVariant].datasets[0]?.additinalInfo) {
      const { filterEntityName } = chartData[chartVariant].datasets[0]?.additinalInfo;
      heading = headingMapForParentId[parentId] + " " + headingMapForChartType[chartType];
      if (filterEntityName) {
        heading = heading + `(${filterEntityName})`;
      }
      return heading;
    }
  };
  return (
    <div className="stnChartContainer main-chart-Chain-child">
      <div className="ChartHeader">
        <div className="chartInfo">{getHeading(parentId, chartType)}</div>
      </div>
      <div className="chartContainer chartCard">
        {labels.length > 0 ? (
          chartVariant === "doughnut" ? (
            <Doughnut data={chartData.doughnut} options={chartOptions} />
          ) : (
            <Chart type="bar" data={chartData.bar} options={chartOptions} />
          )
        ) : (
          <div className="noData">No data</div>
        )}
      </div>
    </div>
  );
};

function ChartChainComponent({
  componentInfo = {},
  date_time_filter = { from: null, to: null },
  selected_customer_id_list = [],
}) {
  const { id, API_URL, params = {}, chart_label: count_label } = componentInfo;
  const [chartVariant, setChartVariant] = useState("doughnut");

  const [chartList, setChartList] = useImmer(["stn"]);
  const [chartData, setChartData] = useImmer(initialChartData);
  const [stationSelecetd, setStationSelected] = useState(null);

  const navigate = useNavigate();

  const navigateTo = (url, searchParams) => {
    navigate({
      pathname: url,
      search: createSearchParams(searchParams).toString(),
    });
  };

  const barChartOption = barChartOptons(
    componentInfo,
    chartData,
    setChartData,
    setChartList,
    stationSelecetd,
    setStationSelected,
    navigateTo,
    chartVariant,
  );

  const global_selected_customer_id_list = useSelector(selectedCustomersIdsSelector);

  const visible_customer_id_list = useMemo(() => {
    if (selected_customer_id_list && selected_customer_id_list.length > 0) return selected_customer_id_list;
    return global_selected_customer_id_list;
  }, [global_selected_customer_id_list, selected_customer_id_list]);

  useEffect(() => {
    getStnChartData();
    setChartList(["stn"]);
  }, [visible_customer_id_list, date_time_filter]);

  const getStnChartData = () => {
    const _params = { assignedCustomerIds: visible_customer_id_list.toString() };
    const URL = `${APP_BASE_URL}${API_URL}`;
    switch (id) {
      case "sfr":
      case "maintenace":
      case "out_of_range": {
        _params.startTs = date_time_filter.from;
        _params.endTs = date_time_filter.to;
        break;
      }
      default: {
      }
    }
    axios.get(URL, { params: { ...params, ..._params } }).then((res) => {
      const API_RESPONSE = res.data.data;
      const stnChartLabel = [];
      const stnChartData = [];
      const customeidToNameMap = {};
      API_RESPONSE.forEach(({ entity, count, entityId }) => {
        stnChartLabel.push(entity);
        stnChartData.push(count);
        customeidToNameMap[entity] = entityId;
      });
      let STN_CHART_DATA = {
        labels: stnChartLabel,
        datasets: [
          {
            label: count_label,
            data: stnChartData,
            backgroundColor: chartColorMap[id]["stn"],
            additinalInfo: {
              chartType: "stn",
              customeidToNameMap: customeidToNameMap,
              stateToBeSet: "dept",
              idToNameMap: customeidToNameMap,
            },
          },
        ],
      };
      let DOUGHNUT_CHART_DATA = {
        labels: stnChartLabel,
        datasets: [
          {
            label: count_label,
            data: stnChartData,
            backgroundColor: stnChartLabel.map((_, index) => generateColorFromNumber(index + 1)),
            borderColor: stnChartLabel.map((_, index) => generateColorFromNumber(index + 1)),
            additinalInfo: {
              chartType: "stn",
              customeidToNameMap: customeidToNameMap,
              stateToBeSet: "dept",
              idToNameMap: customeidToNameMap,
            },
          },
        ],
      };
      setChartData((prev) => {
        prev.stn = { bar: STN_CHART_DATA, doughnut: DOUGHNUT_CHART_DATA };
      });
    });
  };
  const closeCharts = (type) => {
    // let typeIndex = chartList.indexOf(type)
    // console.log('chartList', type, chartList)
    // const newChartList = chartList.splice(0, typeIndex)
    // setChartList([newChartList])
  };
  return (
    <div className="main-chart-chain mainCard">
      <Stack direction="row-reverse" className="mb-2">
        <ButtonGroup>
          <Button variant={chartVariant === "doughnut" ? "contained" : "outlined"} onClick={() => setChartVariant("doughnut")}>
            <PieChartIcon fontSize="small" />
          </Button>
          <Button variant={chartVariant === "bar" ? "contained" : "outlined"} onClick={() => setChartVariant("bar")}>
            <BarChartIcon fontSize="small" />
          </Button>
        </ButtonGroup>
      </Stack>
      {chartList.map((chartType, index) => (
        <ChartChainChild
          key={chartType}
          parentId={id}
          chartType={chartType}
          chartData={chartData[chartType]}
          chartOptions={barChartOption}
          chartVariant={chartVariant}
        />
      ))}
    </div>
  );
}

export default ChartChainComponent;
